import React from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

const KNOWN_FIELDS = [
  { value: 'email', label: 'Email' },
  { value: 'first_name', label: 'First Name' },
  { value: 'last_name', label: 'Last Name' },
  { value: 'company_name', label: 'Company' },
  { value: 'job_title', label: 'Job Title' },
  { value: 'membership_tier', label: 'Membership Tier' },
  { value: 'phone', label: 'Phone' },
  { value: 'country', label: 'Country' },
  { value: 'timezone', label: 'Timezone' },
  { value: 'date_of_birth', label: 'Date of Birth' },
  { value: 'gender', label: 'Gender' },
  { value: 'consent_status', label: 'Consent Status' },
  { value: 'groups', label: 'Groups' },
]

const OPERATORS = [
  { value: 'EQUALS', label: 'Equals' },
  { value: 'NOT_EQUALS', label: 'Does not equal' },
  { value: 'CONTAINS', label: 'Contains' },
  { value: 'NOT_CONTAINS', label: 'Does not contain' },
  { value: 'STARTS_WITH', label: 'Starts with' },
  { value: 'ENDS_WITH', label: 'Ends with' },
  { value: 'GREATER_THAN', label: 'Greater than' },
  { value: 'LESS_THAN', label: 'Less than' },
  { value: 'IS_EMPTY', label: 'Is empty' },
  { value: 'IS_NOT_EMPTY', label: 'Is not empty' },
]

const emptyRule = () => ({ field: 'email', operator: 'EQUALS', value: '', join: 'AND' })

const SegmentRuleBuilder = ({ rules = [], onChange, customFieldDefinitions = [] }) => {
  const updateRule = (index, patch) => {
    const next = rules.map((r, i) => (i === index ? { ...r, ...patch } : r))
    onChange(next)
  }

  const addRule = () => onChange([...rules, emptyRule()])
  const removeRule = (index) => onChange(rules.filter((_, i) => i !== index))

  const needsValue = (operator) => operator !== 'IS_EMPTY' && operator !== 'IS_NOT_EMPTY'

  return (
    <div className="space-y-3">
      {rules.length === 0 && (
        <p className="text-sm text-muted-foreground">No conditions yet. Add one to define this segment's audience.</p>
      )}
      {rules.map((rule, index) => (
        <div key={index} className="flex items-start gap-2 rounded-lg border p-3">
          <div className="flex flex-col gap-2 flex-1">
            {index > 0 && (
              <Select value={rule.join || 'AND'} onValueChange={(v) => updateRule(index, { join: v })}>
                <SelectTrigger className="w-24 h-8 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="AND">AND</SelectItem>
                  <SelectItem value="OR">OR</SelectItem>
                </SelectContent>
              </Select>
            )}
            <div className="grid grid-cols-3 gap-2">
              <Select value={rule.field} onValueChange={(v) => updateRule(index, { field: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Field" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectLabel>Contact Fields</SelectLabel>
                    {KNOWN_FIELDS.map((f) => (
                      <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                    ))}
                  </SelectGroup>
                  {customFieldDefinitions.length > 0 && (
                    <SelectGroup>
                      <SelectLabel>Custom Fields</SelectLabel>
                      {customFieldDefinitions.map((f) => (
                        <SelectItem key={`custom:${f.name}`} value={`custom:${f.name}`}>{f.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  )}
                </SelectContent>
              </Select>
              <Select value={rule.operator} onValueChange={(v) => updateRule(index, { operator: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Operator" />
                </SelectTrigger>
                <SelectContent>
                  {OPERATORS.map((op) => (
                    <SelectItem key={op.value} value={op.value}>{op.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {needsValue(rule.operator) ? (
                rule.field === 'consent_status' ? (
                  <Select value={rule.value} onValueChange={(v) => updateRule(index, { value: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Value" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SUBSCRIBED">Subscribed</SelectItem>
                      <SelectItem value="UNSUBSCRIBED">Unsubscribed</SelectItem>
                      <SelectItem value="PENDING">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    placeholder="Value"
                    value={rule.value ?? ''}
                    onChange={(e) => updateRule(index, { value: e.target.value })}
                  />
                )
              ) : (
                <div />
              )}
            </div>
          </div>
          <Button variant="ghost" size="icon" className="h-8 w-8 mt-1 text-destructive" onClick={() => removeRule(index)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addRule}>
        <Plus className="mr-2 h-4 w-4" />
        Add Condition
      </Button>
    </div>
  )
}

export default SegmentRuleBuilder
